<script setup lang="ts">
import type { TableCellProperties } from "@kaleidoscope-solutions/engine-wasm"
import type { KTabItem } from "@kaleidoscope-solutions/ks-design-system"
import type { Panels } from "@/logic/GridBuilder"
import { useAuth0 } from "@auth0/auth0-vue"
import { KTabs } from "@kaleidoscope-solutions/ks-design-system"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"
import WarningStateImage from "@/assets/pngs/WarningState.png?w=288&h=358&format=webp&imagetools"
import TableGrid from "@/components/Grid/TableGrid.vue"
import { panelsFromCellProperties } from "@/logic/GridBuilder"
import worker from "@/sandbox?worker&url"

import { useListImportStore, useSettingsStore } from "@/stores"
import { captureError, tokenCallback, useEngineModel } from "@/utils/useEngineModel"
import { EngineError } from "@/utils/useEngineModel/error"
import { cloneModelToSandbox, ModelSandboxFacade } from "@/utils/useEngineModel/ModelSandboxFacade"

interface Emits {
  (event: "disableImport", disableImport: boolean): void
}

const emit = defineEmits<Emits>()
const listImportStore = useListImportStore()
const settingsStore = useSettingsStore()

// Register the onUnmounted callback before other logic
// otherwise as the calls in this file are blocking, and if you switch tabs
// quickly, it never runs the unMounted callback as it never gets registered.
let sandbox: ModelSandboxFacade | null = new ModelSandboxFacade(settingsStore.logLevel)
onUnmounted(() => {
  if (sandbox) {
    // We must terminate the worker each time, otherwise the web workers
    // persist and are generated every time you navigate to the preview.
    sandbox.terminateWorker()
    sandbox = null
  }
})
const model = useEngineModel()

const auth0 = useAuth0()
const tables = ref<{ name: string; panels: Panels }[]>([])

const activeTable = ref(0)
const previewFailedMessage = ref("")

const isThousandRowLimitErr = ref(false)
const isDuplicateListItemErr = ref(false)

onMounted(async () => {
  if (!sandbox) {
    return
  }

  try {
    const target = listImportStore.target
    const table = await getPreviewTable(target)
    tables.value.push(table)
  } catch (error) {
    captureError(error)
  }
})

try {
  await sandbox.initialise(tokenCallback(auth0), worker)
  await cloneModelToSandbox(sandbox, model)

  if (listImportStore.importData.targets === undefined) {
    throw new Error("No target selected")
  }

  for (const target of listImportStore.importData.targets) {
    if (listImportStore.importingExternalNames) {
      const list = await sandbox.api.externalNameListNameForType(target)
      await sandbox.api.clearList(list)
    } else {
      const list = await sandbox.api.primaryListForType(target)
      await sandbox.api.clearList(list.name)
    }
  }

  await sandbox.api.import(JSON.stringify(listImportStore.importData))

  previewFailedMessage.value = ""
} catch (error) {
  if (error instanceof EngineError) {
    if (error.messageWithoutCode.match(`only [0-9]+ items for ktype`) !== null) {
      isThousandRowLimitErr.value = true
    }
    if (error.messageWithoutCode.match(`Duplicate list item found`) !== null) {
      isDuplicateListItemErr.value = true
    }
  }
  captureError(error)
}

watch(
  [isThousandRowLimitErr, isDuplicateListItemErr],
  () => {
    emit("disableImport", isThousandRowLimitErr.value || isDuplicateListItemErr.value)
  },
  {
    immediate: true,
  }
)

async function getPreviewTable(target?: string) {
  if (target === undefined) {
    throw new Error("Target is not selected")
  }

  if (sandbox === null) {
    throw new Error("Sandbox not instantiated")
  }

  const table = await sandbox.api.importPreviewTable(target)

  return {
    name: table.name,
    panels: panelsFromCellProperties(
      JSON.parse(table.cellProperties) as TableCellProperties
    ),
  }
}

const tabs = computed<KTabItem[]>(() => {
  return tables.value.map((table, tableIndex) => {
    return {
      text: table.name,
      cb: () => {
        activeTable.value = tableIndex
      },
      active: activeTable.value === tableIndex
    }
  })
})
</script>

<template>
  <div class="flex w-full flex-1 flex-col overflow-auto">
    <div
      v-if="isThousandRowLimitErr || isDuplicateListItemErr"
      class="my-2 flex min-h-96 items-center justify-center rounded-md bg-gray-100"
    >
      <div class="flex items-center">
        <div>
          <img
            :src="WarningStateImage"
            class="w-52 px-8"
            alt=""
          >
        </div>
        <div class="w-80 space-y-4">
          <h2 class="text-lg font-bold">
            Invalid Import
          </h2>
          <p v-if="isThousandRowLimitErr">
            We are not able to handle more than 1000 items in a category, but we
            are working on it.
          </p>
          <p v-if="isDuplicateListItemErr">
            We are not able to import duplicate items.
          </p>
        </div>
      </div>
    </div>

    <div
      v-else-if="tables.length > 0"
      class="flex flex-col gap-6"
    >
      <KTabs
        v-if="tabs.length > 1"
        :items="tabs"
        class="left-0 bg-white whitespace-nowrap"
      />
      <Suspense>
        <template
          v-for="(table, tableIndex) in tables"
          :key="table.name"
        >
          <TableGrid
            v-if="tableIndex === activeTable"
            :name="table.name"
            :panels="(table.panels as Panels)"
          />
        </template>
      </Suspense>
    </div>
  </div>
</template>
